/**
 * Contact Data Store (Pinia)
 *
 * Holds the FULL raw API response from /api/mobile-app/contacts/{contactId}/financial_data/
 * when the portal is opened in app mode (?arkaApp=<contactId>).
 *
 * This store is the single source of truth for:
 *   - report_json  → n_panels, recommended_system_size, panel config
 *   - input_details → latitude, longitude, monthly_bill, etc.
 *   - design_details → persisted studio design data
 *
 * Web mode does NOT use this store — it remains empty and all getters return null/defaults.
 */

import { defineStore } from 'pinia';

export const useContactDataStore = defineStore('contactData', {
    state: () => ({
        /** Whether data has been loaded from the API */
        loaded: false,

        /** The contact ID used to load data */
        contactId: null,

        /** Raw API response fields */
        reportJson: null,
        inputDetails: null,
        designDetails: null,

        /** Full raw response object (for debugging / future use) */
        rawApiData: null,
    }),

    getters: {
        // ── Location ──────────────────────────────────────────────────────
        /** Latitude from API input_details (primary source for app mode ground image) */
        latitude: (state) => {
            const lat = state.inputDetails?.latitude;
            return lat != null ? parseFloat(lat) : null;
        },

        /** Longitude from API input_details */
        longitude: (state) => {
            const lng = state.inputDetails?.longitude;
            return lng != null ? parseFloat(lng) : null;
        },

        /** Zoom level — not typically in API, falls back to 20 */
        zoom: (state) => {
            return state.inputDetails?.zoom ?? 20;
        },

        // ── Panel / Solar Config ──────────────────────────────────────────
        /** Target number of panels from report_json */
        nPanels: (state) => {
            return state.reportJson?.n_panels ?? null;
        },

        /** Recommended system size (kWp) from report_json */
        recommendedSystemSize: (state) => {
            return state.reportJson?.recommended_system_size ?? null;
        },

        /** First panel entry from report_json.panel array */
        panelEntry: (state) => {
            const panels = state.reportJson?.panel;
            if (Array.isArray(panels) && panels.length > 0) {
                return panels[0];
            }
            return null;
        },

        /**
         * Maps the API panel data to the format expected by Subarray:
         *   moduleProperties: { moduleId, moduleLength, moduleMake, moduleSize, moduleWidth }
         *   panelProperties:  { characteristics: {...}, id, model, ... }
         *   tilt, azimuth
         *
         * Returns null if no panel data available.
         */
        subarrayPanelConfig: (state) => {
            const entry = state.reportJson?.panel?.[0];
            if (!entry || !entry.moduleProperties) return null;

            const mp = entry.moduleProperties;

            // API sends dimensions in mm → convert to meters
            const lengthM = (mp.length || 0) > 100 ? mp.length / 1000 : mp.length;
            const widthM  = (mp.width  || 0) > 100 ? mp.width  / 1000 : mp.width;

            const moduleProperties = {
                moduleId:     mp.moduleId ?? entry.id ?? 0,
                moduleLength: lengthM,
                moduleWidth:  widthM,
                moduleMake:   entry.brand || 'Unknown',
                moduleSize:   mp.capacity || 0,   // kW per panel
            };

            const panelProperties = {
                characteristics: {
                    cell_number:  mp.cell_number  || 60,
                    cell_type:    mp.cell_type     || 'Polycrystalline',
                    length:       lengthM,
                    manufacturer: entry.brand      || 'Unknown',
                    model:        entry.model       || '',
                    p_mp_ref:     (mp.capacity || 0) * 1000,   // W
                    series:       entry.model       || '',
                    v_max:        mp.v_max          || 1000,
                    width:        widthM,
                },
                id:         mp.moduleId ?? entry.id ?? 0,
                image:      null,
                image_link: null,
                is_selected: true,
                model:      `${entry.brand || ''} ${entry.model || ''}`.trim(),
            };

            return {
                moduleProperties,
                panelProperties,
                tilt:    entry.tilt    ?? 20,
                azimuth: entry.azimuth ?? 180,
            };
        },
    },

    actions: {
        /**
         * Populate the store from the raw API response.
         * Called once during studio initialization in app mode.
         * API uses snake_case field names: report_json, input_details, design_details
         */
        populate(contactId, apiData) {
            if (!apiData) return;

            this.contactId      = contactId;
            this.reportJson     = apiData.report_json  || null;  // API uses snake_case
            this.inputDetails   = apiData.input_details || null; // API uses snake_case
            this.designDetails  = apiData.design_details || null; // API uses snake_case
            this.rawApiData     = apiData;
            this.loaded         = true;
        },

        /**
         * Update specific fields after a save or partial refresh.
         */
        updateDesignDetails(designDetails) {
            this.designDetails = designDetails;
        },

        updateInputDetails(inputDetails) {
            this.inputDetails = inputDetails;
        },

        updateReportJson(reportJson) {
            this.reportJson = reportJson;
        },

        /** Reset store (e.g. when switching contacts or exiting app mode) */
        $resetStore() {
            this.loaded         = false;
            this.contactId      = null;
            this.reportJson     = null;
            this.inputDetails   = null;
            this.designDetails  = null;
            this.rawApiData     = null;
        },
    },
});
